import { useState, useEffect, useCallback } from 'react';
import '../../../../css/custom-scrollbar.css';
import { useMyContext } from '../../../../context/MyContext';
import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ndrList, Base_Url, ndr, ndrStatusList, ndrAction } from '../../../../config/config';
import { toast } from 'react-toastify';
import FullPageLoader from '../../../../component/fullPageLoader/fullPageLoader';
import Ndrmodel from '../../../../component/b2c/models/ndrModel';
import axios from 'axios';
import {
  ShieldAlert,
  Search,
  X,
  RotateCcw,
  Undo2,
  RefreshCw,
  Copy,
  Check,
  Clock,
  MapPin,
  Phone,
  AlertCircle,
  CheckCircle2,
  Package,
  Layers
} from 'lucide-react';

// Helper: Format Date & Time cleanly
const formatDate = (dateString) => {
  if (!dateString || dateString === 'null' || dateString === '0000-00-00 00:00:00') return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(dateString);
  }
};

// Helper: Format Reason from snake_case to Title Case
const formatReason = (reason) => {
  if (!reason || reason === 'null') return '—';
  if (reason.includes('_')) {
    return reason
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }
  return reason;
};

// Helper: Format Courier Partner with color styles
const formatPartner = (partner) => {
  if (!partner || partner === 'null') {
    return {
      name: 'Standard',
      color: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      dot: 'bg-slate-400'
    };
  }
  const p = String(partner).toLowerCase();
  if (p.includes('delhivery')) {
    return {
      name: 'Delhivery',
      color: 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20',
      dot: 'bg-rose-500'
    };
  }
  if (p.includes('ekart')) {
    return {
      name: 'Ekart',
      color: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20',
      dot: 'bg-blue-500'
    };
  }
  if (p.includes('xpressbees')) {
    return {
      name: 'Xpressbees',
      color: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20',
      dot: 'bg-amber-500'
    };
  }
  if (p.includes('shadowfax')) {
    return {
      name: 'Shadowfax',
      color: 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-500/20',
      dot: 'bg-orange-500'
    };
  }
  return {
    name: partner.charAt(0).toUpperCase() + partner.slice(1),
    color: 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/20',
    dot: 'bg-purple-500'
  };
};

// Helper: Initials from name
const getInitials = (first = '', last = '') => {
  const f = (first || '').trim().charAt(0);
  const l = (last || '').trim().charAt(0);
  return (f + l).toUpperCase() || 'CU';
};

export default function NDRDashboard() {
  const [activeTab, setActiveTab] = useState('Action Pending');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { setorderId, isDarkMode, isToggleSidebar, setIsToggleSidebar } = useMyContext();
  const [limit, setLimit] = useState(10);
  const [ndrListData, setNdrListData] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [originalData, setOriginalData] = useState([]);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [ndrdata, setNdrData] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [tabCounts, setTabCounts] = useState({
    'Action Pending': null,
    'Action Taken': null,
    'RTO Delivered': null,
    'All Details': null,
  });

  const [pagination, setPagination] = useState({
    'Action Pending': 1,
    'Action Taken': 1,
    'RTO Delivered': 1,
    'All Details': 1,
  });

  const navigateForTrack = useNavigate();
  const tabs = ['Action Pending', 'Action Taken', 'RTO Delivered', 'All Details'];
  const token = localStorage.getItem('token');
  const cusid = localStorage.getItem('cus_id');

  const copyToClipboard = (text, id, label = "Text") => {
    if (!text || text === "N/A" || text === "—") return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.info(`${label} copied!`, { autoClose: 1200 });
    }
  };

  const handleViewClick = (item) => {
    const partner = (item.partner || item.order_partner || '').toLowerCase();
    if (partner === "ekart") {
      setNdrData({
        ...item,
        order_partner: item.order_partner || item.partner || 'ekart',
        order_phone: item.order_phone || item.phone || '',
        order_drop_address: item.order_drop_address || item.address || '',
      });
      setIsPopupOpen(true);
    } else {
      handleRemmitance(item);
    }
  };

  const handleRemmitance = async (data) => {
    setIsPageLoading(true);
    const editNdrEndpoint = Base_Url + ndrAction;

    const payload = {
      order_id: data.order_id,
      action: "Re-attempt",
    };

    try {
      const response = await axios.post(editNdrEndpoint, payload, {
        headers: {
          token: token,
          cus_id: cusid,
          "Content-Type": "application/json",
        },
      });

      if (response.data.status === "SUCCESS") {
        fetchNdrListByCurrentTab();
        toast.success(response.data.message || "Re-attempt requested successfully");
      } else {
        toast.error(response.data.message || "Failed to update NDR data");
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.messages && typeof errorData.messages === "object") {
        const detailedErrors = Object.values(errorData.messages).join(", ");
        toast.error(detailedErrors || "Validation error occurred");
      } else {
        toast.error(errorData?.message || "An error occurred while updating NDR data");
      }
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleClose = () => {
    setIsPopupOpen(false);
    setNdrData(ndr);
  };

  const fetchNdrListByCurrentTab = useCallback(async () => {
    const pageNumber = pagination[activeTab];
    const limitNumber = Number(limit) || 10;
    setIsPageLoading(true);
    try {
      let url = '';
      if (activeTab === 'Action Pending') {
        url = `${Base_Url}${ndrList}/Pending?page=${pageNumber}&limit=${limitNumber}`;
      } else if (activeTab === 'Action Taken') {
        url = `${Base_Url}${ndrStatusList}?page=${pageNumber}&limit=${limitNumber}`;
      } else if (activeTab === 'RTO Delivered') {
        url = `${Base_Url}${ndrList}/RTO?page=${pageNumber}&limit=${limitNumber}`;
      } else if (activeTab === 'All Details') {
        url = `${Base_Url}${ndrList}?page=${pageNumber}&limit=${limitNumber}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cusid || '',
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.status === "SUCCESS") {
        const data = Array.isArray(result.data) ? result.data : [];
        const totalItems = result.pagination?.totalItems || result.total || result.totalItems || result.count || data.length || 0;

        setOriginalData(data);
        setNdrListData(data);
        setApiTotalItems(totalItems);
        setTotalOrders(totalItems);
        setTabCounts(prev => ({
          ...prev,
          [activeTab]: totalItems,
        }));
      } else {
        toast.error(result.message || "Failed to fetch data");
        setNdrListData([]);
        setTotalOrders(0);
        setOriginalData([]);
        setApiTotalItems(0);
      }
    } catch (error) {
      console.error('API error:', error.message);
      toast.error("An error occurred while fetching data");
      setNdrListData([]);
      setTotalOrders(0);
      setOriginalData([]);
      setApiTotalItems(0);
    } finally {
      setIsPageLoading(false);
      setIsRefreshing(false);
    }
  }, [activeTab, pagination, limit, token, cusid]);

  useEffect(() => {
    fetchNdrListByCurrentTab();
  }, [fetchNdrListByCurrentTab]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchNdrListByCurrentTab();
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setNdrListData(originalData);
      setTotalOrders(apiTotalItems);
    } else {
      const q = searchQuery.toLowerCase().trim();
      const filtered = originalData.filter(item => {
        const orderId = String(item.order_id || '').toLowerCase();
        const waybill = String(item.waybill || item.awb_number || '').toLowerCase();
        const customerName = `${item.order_first_name || ''} ${item.order_last_name || ''}`.toLowerCase();
        const phone = String(item.order_phone || item.phone || '').toLowerCase();
        const reason = String(item.reason || '').toLowerCase();
        const partner = String(item.partner || item.order_partner || '').toLowerCase();
        const action = String(item.action || '').toLowerCase();
        const city = String(item.order_drop_city || '').toLowerCase();
        return (
          orderId.includes(q) ||
          waybill.includes(q) ||
          customerName.includes(q) ||
          phone.includes(q) ||
          reason.includes(q) ||
          partner.includes(q) ||
          action.includes(q) ||
          city.includes(q)
        );
      });
      setNdrListData(filtered);
      setTotalOrders(filtered.length);
    }
  }, [searchQuery, originalData, apiTotalItems]);

  const renderTableHeaders = () => {
    const commonHeaders = [
      'Order ID',
      'AWB No.',
      'Customer Info',
      'Courier',
      'NDR Count',
      'Reason',
      'Last Updated',
    ];

    if (activeTab === 'Action Pending') {
      return [...commonHeaders, 'Action'];
    } else if (activeTab === 'Action Taken') {
      return [...commonHeaders, 'Action Taken'];
    } else if (activeTab === 'RTO Delivered') {
      return [...commonHeaders, 'Status'];
    } else if (activeTab === 'All Details') {
      return [...commonHeaders, 'Status'];
    }
    return commonHeaders;
  };

  const handlePaginationChange = (event, value) => {
    setPagination(prev => ({
      ...prev,
      [activeTab]: value,
    }));
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPagination(prev => ({
      ...prev,
      [activeTab]: 1,
    }));
  };

  const handleReturn = async (order_id, action) => {
    try {
      const urlPost = Base_Url + ndrAction;
      setIsPageLoading(true);
      const response = await fetch(urlPost, {
        method: 'POST',
        headers: {
          token: token,
          cus_id: cusid || '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: order_id,
          action: action
        }),
      });

      const result = await response.json();

      if (result.status === "SUCCESS") {
        fetchNdrListByCurrentTab();
        toast.success(result.message || "RTO marked successfully");
      } else {
        toast.error(result.message || "Action failed");
      }
    } catch (error) {
      toast.error("An error occurred while processing your request");
      console.error('API error:', error.message);
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleClick = (orderID) => {
    if (!orderID) return;
    setorderId(orderID);
    navigateForTrack("/shipments/tracking");
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const TAB_CONFIG = {
    'Action Pending': {
      icon: AlertCircle,
      badgeColor: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
      activeGradient: 'from-amber-600 via-rose-600 to-indigo-600',
    },
    'Action Taken': {
      icon: CheckCircle2,
      badgeColor: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
      activeGradient: 'from-emerald-600 to-teal-600',
    },
    'RTO Delivered': {
      icon: Undo2,
      badgeColor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
      activeGradient: 'from-purple-600 to-indigo-600',
    },
    'All Details': {
      icon: Layers,
      badgeColor: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      activeGradient: 'from-indigo-600 via-purple-600 to-indigo-700',
    },
  };

  const renderTableRow = (item, index) => {
    const rawDate = item.timestamp || item.updated_at || item.created_at || item.date;
    const partnerInfo = formatPartner(item.partner || item.order_partner);
    const hasCustomer = item.order_first_name || item.order_phone;
    const initials = getInitials(item.order_first_name, item.order_last_name);
    const isOrderCopied = copiedId === `order-${item.order_id}-${index}`;
    const isAwbCopied = copiedId === `awb-${item.waybill || item.awb_number}-${index}`;

    const commonCells = [
      // 1. Order ID
      <td key="orderId" className="py-2.5 px-2.5 text-xs whitespace-nowrap">
        <div className="flex items-center gap-1 group">
          <button
            onClick={() => handleClick(item.order_id)}
            className="font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline decoration-1 underline-offset-2 transition-colors flex items-center gap-1"
            title="Click to track shipment"
          >
            <Package size={12} className="text-indigo-400 shrink-0" />
            <span>{item.order_id || "—"}</span>
          </button>
          {item.order_id && (
            <button
              onClick={(e) => { e.stopPropagation(); copyToClipboard(item.order_id, `order-${item.order_id}-${index}`, "Order ID"); }}
              className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors p-0.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Copy Order ID"
            >
              {isOrderCopied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
            </button>
          )}
        </div>
      </td>,

      // 2. AWB No.
      <td key="awbNo" className="py-2.5 px-2.5 text-xs whitespace-nowrap">
        <div className="flex items-center gap-1 font-mono">
          <span className="font-semibold text-slate-800 dark:text-slate-200 tracking-tight text-[11px]">
            {item.waybill || item.awb_number || "—"}
          </span>
          {(item.waybill || item.awb_number) && (
            <button
              onClick={(e) => { e.stopPropagation(); copyToClipboard(item.waybill || item.awb_number, `awb-${item.waybill || item.awb_number}-${index}`, "AWB No."); }}
              className="text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors p-0.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Copy AWB No."
            >
              {isAwbCopied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
            </button>
          )}
        </div>
      </td>,

      // 3. Customer Info (Initials avatar + Name + Phone + City)
      <td key="customer" className="py-2.5 px-2.5 text-xs">
        {hasCustomer ? (
          <div className="flex items-center gap-2 max-w-[150px]">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black bg-gradient-to-tr from-indigo-500/15 via-purple-500/15 to-pink-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60 shrink-0 shadow-2xs">
              {initials}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-slate-900 dark:text-white truncate leading-tight text-[11px]">
                {`${item.order_first_name || ''} ${item.order_last_name || ''}`.trim() || 'Customer'}
              </span>
              <div className="flex items-center gap-1 text-[9.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                {item.order_phone && (
                  <span className="truncate">{item.order_phone}</span>
                )}
                {item.order_drop_city && (
                  <span className="truncate">• {item.order_drop_city}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>,

      // 4. Courier Partner
      <td key="courier" className="py-2.5 px-2.5 text-xs whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shadow-2xs ${partnerInfo.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${partnerInfo.dot}`} />
          {partnerInfo.name}
        </span>
      </td>,

      // 5. NDR Count (Attempts)
      <td key="count" className="py-2.5 px-2.5 text-xs whitespace-nowrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
          Number(item.count) > 0
            ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30 ring-1 ring-amber-500/20'
            : 'bg-slate-100 dark:bg-slate-800/70 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700'
        }`}>
          {item.count !== undefined && item.count !== null ? `${item.count} Attempt(s)` : '0 Attempt(s)'}
        </span>
      </td>,

      // 6. Reason
      <td key="reason" className="py-2.5 px-2.5 text-xs">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium border max-w-[150px] truncate ${
          isDarkMode
            ? 'bg-slate-800/80 text-slate-300 border-slate-700/80'
            : 'bg-slate-50 text-slate-700 border-slate-200'
        }`} title={item.reason}>
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
          <span className="truncate">{formatReason(item.reason)}</span>
        </span>
      </td>,

      // 7. Last Updated Date & Time
      <td key="lastUpdated" className="py-2.5 px-2.5 text-[10.5px] whitespace-nowrap font-mono text-slate-600 dark:text-slate-400">
        <div className="flex items-center gap-1">
          <Clock size={10} className="text-slate-400 shrink-0" />
          <span>{formatDate(rawDate)}</span>
        </div>
      </td>,
    ];

    if (activeTab === 'Action Pending') {
      commonCells.push(
        <td key="action" className="py-2.5 px-2.5 text-xs whitespace-nowrap text-right">
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => handleViewClick(item)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-bold rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-sm shadow-indigo-600/20 active:scale-95 transition-all"
            >
              <RotateCcw size={11} />
              <span>Reattempt</span>
            </button>
            <button
              onClick={() => handleReturn(item.order_id, 'Return')}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-bold rounded-lg text-white bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 shadow-sm shadow-rose-600/20 active:scale-95 transition-all"
            >
              <Undo2 size={11} />
              <span>Return</span>
            </button>
          </div>
        </td>
      );
    } else if (activeTab === 'Action Taken') {
      commonCells.push(
        <td key="action" className="py-2.5 px-2.5 text-xs whitespace-nowrap text-right">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30 shadow-2xs">
            <CheckCircle2 size={11} className="text-emerald-500" />
            <span>{formatReason(item.action || "Action Taken")}</span>
          </span>
        </td>
      );
    } else if (activeTab === 'RTO Delivered') {
      commonCells.push(
        <td key="action" className="py-2.5 px-2.5 text-xs whitespace-nowrap text-right">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-500/30 shadow-2xs">
            <Undo2 size={11} className="text-purple-500" />
            <span>RTO Delivered</span>
          </span>
        </td>
      );
    } else if (activeTab === 'All Details') {
      commonCells.push(
        <td key="action" className="py-2.5 px-2.5 text-xs whitespace-nowrap text-right">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 shadow-2xs">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            <span>{formatReason(item.action || "Dispatched")}</span>
          </span>
        </td>
      );
    }

    return (
      <tr
        key={index}
        className={`transition-colors duration-150 border-b ${
          isDarkMode
            ? 'border-slate-800/80 hover:bg-indigo-500/[0.04]'
            : 'border-slate-100 hover:bg-indigo-50/40'
        }`}
      >
        {commonCells}
      </tr>
    );
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({
      ...prev,
      [tab]: 1,
    }));
    setSearchQuery('');
  };

  const NoDataMessage = () => (
    <tr>
      <td colSpan={renderTableHeaders().length} className="py-16 text-center">
        <div className="flex flex-col items-center justify-center gap-2.5">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800/60 flex items-center justify-center text-indigo-500 dark:text-indigo-400 shadow-sm">
            <Search size={22} />
          </div>
          <div className="space-y-0.5">
            <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
              {searchQuery ? 'No matching NDR records found' : 'No records available in this tab'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {searchQuery ? 'Try searching with another keyword or clear filter' : 'Reports will appear here once non-delivery events are logged'}
            </p>
          </div>
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="mt-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Clear search filter
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const getPaginationCount = () => {
    if (searchQuery.trim() !== '') {
      return 1;
    }
    return Math.ceil(apiTotalItems / limit) || 1;
  };

  // ===== MOBILE CARD RENDERER =====
  const renderMobileCard = (item, index) => {
    const rawDate = item.timestamp || item.updated_at || item.created_at || item.date;
    const partnerInfo = formatPartner(item.partner || item.order_partner);
    const hasCustomer = item.order_first_name || item.order_phone;
    const isOrderCopied = copiedId === `mob-order-${item.order_id}-${index}`;
    const isAwbCopied = copiedId === `mob-awb-${item.waybill || item.awb_number}-${index}`;

    return (
      <div
        key={index}
        className={`rounded-2xl border p-4 transition-all duration-200 space-y-3 shadow-xs ${
          isDarkMode
            ? 'bg-slate-900/80 border-slate-800 hover:border-indigo-500/40 shadow-[0_4px_20px_rgba(0,0,0,0.3)]'
            : 'bg-white border-slate-200 hover:border-indigo-300 shadow-sm'
        }`}
      >
        {/* Top: Order ID + AWB */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
              Order ID
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => handleClick(item.order_id)}
                className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 truncate"
              >
                <Package size={13} className="shrink-0" />
                <span className="truncate">{item.order_id || "—"}</span>
              </button>
              {item.order_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); copyToClipboard(item.order_id, `mob-order-${item.order_id}-${index}`, "Order ID"); }}
                  className="text-slate-400 hover:text-indigo-600 p-1"
                >
                  {isOrderCopied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                </button>
              )}
            </div>
          </div>
          <div className="text-right shrink-0 max-w-[45%]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
              AWB No.
            </p>
            <div className="flex items-center justify-end gap-1 font-mono">
              <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                {item.waybill || item.awb_number || "—"}
              </span>
              {(item.waybill || item.awb_number) && (
                <button
                  onClick={(e) => { e.stopPropagation(); copyToClipboard(item.waybill || item.awb_number, `mob-awb-${item.waybill || item.awb_number}-${index}`, "AWB No."); }}
                  className="text-slate-400 hover:text-indigo-600 p-1"
                >
                  {isAwbCopied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Customer & Courier Row */}
        {(hasCustomer || partnerInfo.name !== '—') && (
          <div className={`flex items-center justify-between gap-2 pt-2.5 border-t text-xs ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
            <div className="min-w-0">
              <p className="font-bold text-slate-900 dark:text-white truncate">
                {`${item.order_first_name || ''} ${item.order_last_name || ''}`.trim() || 'Customer'}
              </p>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                {item.order_phone && (
                  <span className="flex items-center gap-0.5">
                    <Phone size={10} />
                    <span>{item.order_phone}</span>
                  </span>
                )}
                {item.order_drop_city && (
                  <span className="flex items-center gap-0.5 truncate">
                    <span>•</span>
                    <MapPin size={10} />
                    <span className="truncate">{item.order_drop_city}</span>
                  </span>
                )}
              </div>
            </div>
            <span className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border ${partnerInfo.color}`}>
              {partnerInfo.name}
            </span>
          </div>
        )}

        {/* NDR Count + Reason */}
        <div className={`flex items-center justify-between gap-2 py-2 border-y ${isDarkMode ? 'border-slate-800' : 'border-slate-100'}`}>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
              NDR Count
            </p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold ${
              Number(item.count) > 0
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}>
              {item.count !== undefined && item.count !== null ? `${item.count} Attempt(s)` : '0 Attempt(s)'}
            </span>
          </div>
          <div className="text-right min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
              Reason
            </p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border truncate max-w-[160px] ${
              isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-50 text-slate-700 border-slate-200'
            }`}>
              {formatReason(item.reason)}
            </span>
          </div>
        </div>

        {/* Bottom: Last Updated + Actions */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
              Last Updated
            </p>
            <p className="text-[10.5px] font-mono text-slate-500 dark:text-slate-400">
              {formatDate(rawDate)}
            </p>
          </div>

          {activeTab === 'Action Pending' && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => handleViewClick(item)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 shadow-sm shadow-indigo-600/20 active:scale-95 transition-all"
              >
                <RotateCcw size={11} />
                <span>Reattempt</span>
              </button>
              <button
                onClick={() => handleReturn(item.order_id, 'Return')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-xl text-white bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 shadow-sm shadow-rose-600/20 active:scale-95 transition-all"
              >
                <Undo2 size={11} />
                <span>Return</span>
              </button>
            </div>
          )}

          {activeTab === 'Action Taken' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30">
              <CheckCircle2 size={11} className="text-emerald-500" />
              <span>{formatReason(item.action || "Action Taken")}</span>
            </span>
          )}

          {activeTab === 'RTO Delivered' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-500/30">
              <Undo2 size={11} className="text-purple-500" />
              <span>RTO Delivered</span>
            </span>
          )}

          {activeTab === 'All Details' && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700">
              {formatReason(item.action || "Dispatched")}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`relative w-full max-w-full h-screen overflow-hidden flex flex-col transition-colors duration-300 ${isDarkMode ? 'dark bg-[#080D1A]' : 'bg-slate-100'}`}>
      {/* Ambient background glows strictly contained to prevent horizontal scroll */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15 blur-3xl" />
        <div className="absolute top-1/2 -right-24 h-96 w-96 rounded-full bg-purple-500/10 dark:bg-purple-500/15 blur-3xl" />
      </div>

      {/* Top Navbar */}
      <NavBar />

      <div className="flex flex-1 min-h-0 min-w-0 w-full overflow-hidden relative z-10">
        {isToggleSidebar && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
            onClick={() => setIsToggleSidebar(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`
            fixed inset-y-0 left-0 z-50 w-64
            ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
            md:static md:inset-auto md:z-auto md:translate-x-0
            ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
            m-2 sm:m-3 rounded-3xl overflow-hidden transition-all duration-300 ease-in-out shrink-0
          `}
        >
          <Sidebar />
        </div>

        <FullPageLoader isLoading={isPageLoading} message="Processing your request..." />

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden p-2 sm:p-2.5 md:p-3">
          <div
            className={`flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden rounded-2xl border p-2.5 sm:p-3 md:p-3.5 transition-all duration-300 shadow-sm ${
              isDarkMode
                ? 'bg-gradient-to-br from-[#111827] via-[#0F172A] to-[#0B1120] border-slate-800 shadow-[0_0_40px_rgba(0,0,0,0.5)]'
                : 'bg-white border-slate-200/90 shadow-lg shadow-slate-100'
            }`}
          >
            {/* 1. TOP HEADER + SEARCH + CONTROLS */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3.5 shrink-0">
              {/* Left: Icon, Title & Subtitle */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-rose-500 via-indigo-600 to-purple-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/25 ring-2 ring-white/20">
                  <ShieldAlert size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-base sm:text-lg font-black tracking-tight text-slate-900 dark:text-white">
                      NDR & Dispute Management
                    </h1>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                      {totalOrders} {searchQuery ? 'Filtered' : 'Total'} Records
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Real-time non-delivery reports, buyer reattempts & RTO decision workflow
                  </p>
                </div>
              </div>

              {/* Right: Search Bar + Refresh */}
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                {/* Search Box */}
                <div className="relative flex-1 sm:w-64 md:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search by Order ID, AWB, Customer, Phone..."
                    className={`w-full pl-8 pr-7 py-2 text-xs rounded-xl border outline-none transition-all ${
                      isDarkMode
                        ? 'bg-slate-900/80 border-slate-700/80 text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20'
                        : 'bg-slate-50/70 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 shadow-2xs'
                    }`}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing || isPageLoading}
                  className={`p-2 rounded-xl border transition-all active:scale-95 ${
                    isDarkMode
                      ? 'border-slate-700 bg-slate-800/90 text-slate-300 hover:bg-slate-700 hover:text-white'
                      : 'border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                  }`}
                  title="Refresh NDR List"
                >
                  <RefreshCw size={15} className={isRefreshing ? 'animate-spin text-indigo-500' : ''} />
                </button>
              </div>
            </div>

            {/* 2. SEGMENTED TABS (Sleek Glassmorphic Bar) */}
            <div
              className={`flex-shrink-0 p-1 rounded-2xl border flex gap-1.5 overflow-x-auto mb-3.5 transition-colors duration-300 ${
                isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-100/90 border-slate-200'
              }`}
            >
              {tabs.map((tab) => {
                const config = TAB_CONFIG[tab] || TAB_CONFIG['Action Pending'];
                const TabIcon = config.icon;
                const isActive = activeTab === tab;
                const currentCount = tabCounts[tab] !== null ? tabCounts[tab] : (isActive ? totalOrders : null);

                return (
                  <button
                    key={tab}
                    onClick={() => handleTabClick(tab)}
                    className={`flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl text-xs whitespace-nowrap transition-all duration-200 flex-1 ${
                      isActive
                        ? `bg-gradient-to-r ${config.activeGradient} text-white shadow-md shadow-indigo-600/25 font-bold scale-[1.01]`
                        : `text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-800/60 font-medium`
                    }`}
                  >
                    <TabIcon size={14} className={isActive ? 'text-white' : 'text-slate-400'} />
                    <span>{tab}</span>
                    {currentCount !== null && (
                      <span
                        className={`text-[10px] px-2 py-0.2 rounded-full font-bold leading-tight ${
                          isActive
                            ? 'bg-white/20 text-white backdrop-blur-xs'
                            : 'bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {currentCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 3. DESKTOP DATA TABLE (hidden below md) */}
            <div
              className={`hidden md:flex flex-1 min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border transition-colors duration-300 ${
                isDarkMode
                  ? 'bg-slate-900/40 border-slate-800/90'
                  : 'bg-white border-slate-200 shadow-2xs'
              }`}
            >
              <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-auto custom-scrollbar">
                <table className="w-full min-w-full table-auto border-collapse">
                  <thead
                    className={`sticky top-0 z-10 text-[11px] font-bold uppercase tracking-wider backdrop-blur-md transition-colors duration-300 ${
                      isDarkMode
                        ? 'bg-slate-900/95 text-slate-300 border-b border-slate-800'
                        : 'bg-slate-50/95 text-slate-600 border-b border-slate-200'
                    }`}
                  >
                    <tr>
                      {renderTableHeaders().map((header, index, arr) => (
                        <th
                          key={index}
                          className={`py-2.5 px-2.5 font-bold tracking-wider whitespace-nowrap text-[10.5px] ${
                            index === arr.length - 1 ? 'text-right pr-4' : 'text-left'
                          }`}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                    {ndrListData.length > 0 ? (
                      ndrListData.map((item, index) => renderTableRow(item, index))
                    ) : (
                      <NoDataMessage />
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 4. MOBILE CARDS (shown below md) */}
            <div className="md:hidden flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-2.5 pb-2">
              {ndrListData.length > 0 ? (
                ndrListData.map((item, index) => renderMobileCard(item, index))
              ) : (
                <div className="py-16 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-500 mx-auto mb-2">
                    <Search size={22} />
                  </div>
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-200">No records available</p>
                </div>
              )}
            </div>

            {/* 5. FOOTER TOOLBAR: RECORD SUMMARY + LIMIT + PAGINATION */}
            <div
              className={`flex-shrink-0 mt-2.5 pt-2.5 border-t flex flex-col sm:flex-row justify-between items-center gap-2.5 transition-colors duration-300 ${
                isDarkMode ? 'border-slate-800' : 'border-slate-200'
              }`}
            >
              {/* Record Summary */}
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                <span>
                  {searchQuery ? (
                    <>Showing <span className="text-indigo-600 dark:text-indigo-400 font-bold">{totalOrders}</span> of {apiTotalItems} records</>
                  ) : (
                    <>Showing <span className="text-indigo-600 dark:text-indigo-400 font-bold">{ndrListData.length}</span> of {totalOrders} records</>
                  )}
                </span>
              </div>

              {/* Limit selector & Material UI pagination */}
              <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                  <span className="text-[11px] font-medium">Rows:</span>
                  <select
                    id="limit"
                    value={limit}
                    onChange={handleLimitChange}
                    className={`border rounded-xl px-2 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all ${
                      isDarkMode
                        ? 'bg-slate-900 border-slate-700 text-slate-200'
                        : 'bg-white border-slate-200 text-slate-800 shadow-2xs'
                    }`}
                    disabled={searchQuery.trim() !== ''}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={totalOrders > 0 ? totalOrders : 100}>All ({totalOrders})</option>
                  </select>
                </div>

                <Pagination
                  count={getPaginationCount()}
                  page={searchQuery.trim() !== '' ? 1 : pagination[activeTab]}
                  onChange={handlePaginationChange}
                  variant="outlined"
                  shape="rounded"
                  size="small"
                  disabled={searchQuery.trim() !== ''}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontSize: '11px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      borderColor: isDarkMode ? '#334155' : '#e2e8f0',
                      color: isDarkMode ? '#cbd5e1' : '#475569',
                      minWidth: '28px',
                      height: '28px',
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        color: '#ffffff',
                        borderColor: '#6366f1',
                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.35)',
                      },
                      '&:hover': {
                        backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                      }
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ekart Reattempt Modal */}
      <Ndrmodel isopen={isPopupOpen} onClose={handleClose} ndrdata={ndrdata} />
    </div>
  );
}